import * as React from 'react';
import { ApSimpleDropdown } from '@alixpartners/ui-components';
import * as htmlToImage from 'html-to-image';
import { BreakdownChart } from 'components/BreakdownChart';
import { HeaderDropdown } from 'components/HeaderDropdown';
import { ComparisonWidget } from 'components/Widgets';
import Collapsible from 'components/Collapsible';
import { BreakdownTable } from 'components/Tables/BreakdownTable';
import { CollapsibleHeader } from 'components/Tables/Table.styles';
import {
  SectionWrapper,
  ComparisonWidgetsContainer,
  HeaderContainer,
  HeaderDropdownContainer,
  HeaderTop,
  HeaderLabel,
} from './Analysis.styles';
import { ApiContext } from 'context/ApiContext';
import {
  IBreakdownByResponse,
  IBreakdownPeriod,
  IBreakdownSomething,
  IBreakdownTable,
  ICurrencyFormat,
  IQueryFilter,
  ISpendByType,
} from 'utils/types';
import { BREAKDOWN_BY, CONTRIBUTIONS, PERMISSIONS } from 'utils/constants';
import { getDateLabels } from 'utils/format';
import QueryParams, { isInQueryParamsArray } from 'context/QueryParams';
import { EXPORT_OPTIONS } from 'utils/constants';
import { downloadAsFile } from 'utils/download';
import moment from 'moment';

const EXPENSE_OPTION = {
  label: 'OPEX/CAPEX',
  value: BREAKDOWN_BY.EXPENSE_TYPE,
};
const CONTRIBUTION_OPTION = {
  label: 'Personnel/Third Party',
  value: BREAKDOWN_BY.CONTRIBUTION,
};

const GA_OPTION = {
  label: 'G&A/Non-G&A',
  value: BREAKDOWN_BY.GA,
};

const BREAKDOWN_LABELS = {
  contributionsBreakdown: ['Personnel', 'Third party'],
  expenseTypesBreakdown: ['Opex', 'Capex'],
  spendTypesBreakdown: ['G&A', 'Non-G&A'],
};

const BREAKDOWN_BY_TO_API: {
  [key: string]: string;
} = {
  contributionsBreakdown: 'Contribution',
  expenseTypesBreakdown: 'ExpenseType',
  spendTypesBreakdown: 'SpendType',
};

const findValue = (items: IBreakdownSomething[], label: string): number => {
  const foundItem = items.find((el) => el.dimValue === label);
  return foundItem ? foundItem.percentageOfTotalCost : 0;
};

const getTableData = (
  items: IBreakdownTable[],
  key: string,
  labels: string[]
) => {
  return items.map((item) => {
    const buKey =
      key === 'contributionsBreakdown'
        ? 'costsByContribution'
        : 'costsByExpenseType';
    const buData = item[buKey];
    const some = buData.reduce(
      (acc, buDataItem) => {
        const index = labels.findIndex(
          (label) => label === buDataItem.dimValue
        );
        acc[`category${index + 1}CurrYear`] = buDataItem.currentValue;
        acc[`category${index + 1}PrevYear`] = buDataItem.comparisonValue;
        acc[`category${index + 1}Variance`] = buDataItem.change;
        return acc;
      },
      {} as {
        [x: string]: string | number | null | undefined;
      }
    );
    const result = {
      title: item.dimValue,
      category1CurrYear: null,
      category1PrevYear: null,
      category1Variance: null,
      category2CurrYear: null,
      category2PrevYear: null,
      category2Variance: null,
      totalCurrYear: item.currentTotal,
      totalPrevYear: item.comparisonTotal,
      totalVariance: item.totalChange,
      ...some,
    };
    return result;
  });
};

const getBarsData = (
  breakdownData: IBreakdownByResponse,
  key: keyof Pick<
    IBreakdownPeriod,
    'contributionsBreakdown' | 'expenseTypesBreakdown' | 'spendTypesBreakdown'
  >
): {
  labels: string[];
  startPeriodValues: {
    left: number;
    right: number;
  };
  endPeriodValues: {
    left: number;
    right: number;
  };
} => {
  const labels = BREAKDOWN_LABELS[key];
  const currentPeriodLeftValue = findValue(
    breakdownData.currentPeriod[key],
    labels[0]
  );
  const currentPeriodRightValue = findValue(
    breakdownData.currentPeriod[key],
    labels[1]
  );
  const comparisonPeriodLeftValue = findValue(
    breakdownData.comparisonPeriod[key],
    labels[0]
  );
  const comparisonPeriodRightValue = findValue(
    breakdownData.comparisonPeriod[key],
    labels[1]
  );
  return {
    labels,
    startPeriodValues: {
      left: currentPeriodLeftValue,
      right: currentPeriodRightValue,
    },
    endPeriodValues: {
      left: comparisonPeriodLeftValue,
      right: comparisonPeriodRightValue,
    },
  };
};

const BreakDownByView = ({
  breakdownBy,
  downloadSpendBreakdownChartData,
  downloadSpendBreakdownBusinessUnitData,
  downloadSpendGeographyData,
  format,
  isOnlyContribution,
  params,
  permissions,
  showSuccessNotification,
  selectedBreakdownBy,
  spendBy,
  updateSelectedBreakdownBy,
}: {
  breakdownBy?: IBreakdownByResponse;
  downloadSpendBreakdownChartData: (d: string) => Promise<any>;
  downloadSpendBreakdownBusinessUnitData: (d: string) => Promise<any>;
  downloadSpendGeographyData: (d: string) => Promise<any>;
  format: ICurrencyFormat;
  isOnlyContribution: boolean;
  params: IQueryFilter;
  permissions: string[];
  selectedBreakdownBy: string;
  showSuccessNotification: () => void;
  spendBy: ISpendByType;
  updateSelectedBreakdownBy: (s: string) => void;
}) => {
  const isPersonnelDisabled = !permissions.includes(PERMISSIONS.VIEW_PERSONNEL);
  const isPersonnel =
    isInQueryParamsArray({ contributions: CONTRIBUTIONS.PERSONNEL }, params) &&
    !isPersonnelDisabled;
  const isThirdParty = isInQueryParamsArray(
    { contributions: CONTRIBUTIONS.THIRD_PARTY },
    params
  );
  const isVendorSpendSelected = spendBy === ISpendByType.VENDORS;
  const isContributionFilter =
    (isPersonnel || isThirdParty) && !(isPersonnel && isThirdParty);
  const isContributionOptionDisabled =
    isContributionFilter ||
    isVendorSpendSelected ||
    isOnlyContribution ||
    isPersonnelDisabled;
  const breakdownByOptions = [
    EXPENSE_OPTION,
    {
      ...CONTRIBUTION_OPTION,
      disabled: isContributionOptionDisabled,
    },
    GA_OPTION,
  ];
  const [highlightedPart, setHighlightedPart] = React.useState<number[] | null>(
    null
  );
  const id = `breakdown-by-chart-${new Date().getTime()}`;
  if (!breakdownBy) {
    return null;
  }
  const getSelectedBreakdownBy = () => {
    if (
      selectedBreakdownBy === BREAKDOWN_BY.CONTRIBUTION &&
      isContributionOptionDisabled
    ) {
      return EXPENSE_OPTION.value;
    }
    return selectedBreakdownBy;
  };
  const selectedBreakdownByLocal = getSelectedBreakdownBy();

  const { currentPeriod, comparisonPeriod } = breakdownBy;
  const key = selectedBreakdownByLocal as keyof Pick<
    IBreakdownPeriod,
    'contributionsBreakdown' | 'expenseTypesBreakdown' | 'spendTypesBreakdown'
  >;
  const barData = getBarsData(breakdownBy, key);
  const tableDateLabels = getDateLabels(
    breakdownBy.currentPeriod,
    breakdownBy.comparisonPeriod
  );

  let yearOnYearWidgetData = [
    {
      title: 'OPEX YOY COMPARISON',
      ...breakdownBy.opexYoYComparisonWidget,
    },
    {
      title: 'CAPEX YOY COMPARISON',
      ...breakdownBy.capexYoYComparisonWidget,
    },
  ];
  let quarterlyWidgetData = [
    {
      title: 'OPEX QUARTERLY MOVEMENT',
      ...breakdownBy.opexQuarterlyMovementWidget,
    },
    {
      title: 'CAPEX QUARTERLY MOVEMENT',
      ...breakdownBy.capexQuarterlyMovementWidget,
    },
  ];
  if (key === 'contributionsBreakdown') {
    yearOnYearWidgetData = [
      {
        title: 'PERSONNEL YOY COMPARISON',
        ...breakdownBy.personnelYoYComparisonWidget,
      },
      {
        title: 'THIRD PARTY YOY COMPARISON',
        ...breakdownBy.thirdPartyYoYComparisonWidget,
      },
    ];
    quarterlyWidgetData = [
      {
        title: 'PERSONNEL QUARTERLY MOVEMENT',
        ...breakdownBy.personnelQuarterlyMovementWidget,
      },
      {
        title: 'THIRD PARTY QUARTERLY MOVEMENT',
        ...breakdownBy.thirdPartyQuarterlyMovementWidget,
      },
    ];
  }
  if (key === 'spendTypesBreakdown') {
    yearOnYearWidgetData = [
      {
        title: 'G&A YOY COMPARISON',
        ...breakdownBy.g_AYoYComparisonWidget,
      },
      {
        title: 'Non G&A YOY COMPARISON',
        ...breakdownBy.nonG_AYoYComparisonWidget,
      },
    ];
    quarterlyWidgetData = [
      {
        title: 'G&A QUARTERLY MOVEMENT',
        ...breakdownBy.g_AQuarterlyMovementWidget,
      },
      {
        title: 'Non G&A QUARTERLY MOVEMENT',
        ...breakdownBy.nonG_AQuarterlyMovementWidget,
      },
    ];
  }
  const breakDownApi = BREAKDOWN_BY_TO_API[selectedBreakdownBy];
  const onExportHtmlChartAsPngClick = () => {
    const chart = document.getElementById(id);
    if (chart) {
      htmlToImage.toPng(chart).then((dataUrl) => {
        downloadAsFile(
          dataUrl,
          `TXR_breakdown-by-${breakDownApi}-${moment().format()}`
        );
      });
    }
  };

  return (
    <SectionWrapper>
      <HeaderTop>
        <HeaderContainer>
          <HeaderLabel>Breakdown by </HeaderLabel>
          <HeaderDropdownContainer>
            <HeaderDropdown
              options={breakdownByOptions}
              selected={
                breakdownByOptions.find(
                  (opt) => opt.value === selectedBreakdownBy
                ) || EXPENSE_OPTION
              }
              onChange={(opt) => updateSelectedBreakdownBy(opt.value)}
            />
          </HeaderDropdownContainer>
        </HeaderContainer>
        <ApSimpleDropdown
          iconName="outline_save_alt"
          onChange={(s: any) => {
            if (s.value === 'png') {
              onExportHtmlChartAsPngClick();
            } else if (s.value === 'xlsx') {
              downloadSpendBreakdownChartData(breakDownApi).then(
                showSuccessNotification
              );
            }
          }}
          options={EXPORT_OPTIONS}
          label="Export"
        />
      </HeaderTop>

      <BreakdownChart
        id={id}
        leftLabel={barData.labels[0] || ''}
        rightLabel={barData.labels[1] || ''}
        startPeriod={{
          startDate: currentPeriod.startDate,
          endDate: currentPeriod.endDate,
          leftValue: barData.startPeriodValues.left,
          rightValue: barData.startPeriodValues.right,
        }}
        endPeriod={{
          startDate: comparisonPeriod.startDate,
          endDate: comparisonPeriod.endDate,
          leftValue: barData.endPeriodValues.left,
          rightValue: barData.endPeriodValues.right,
        }}
        onHover={(h) => setHighlightedPart(h)}
      />
      <ComparisonWidgetsContainer>
        <ComparisonWidget
          data={yearOnYearWidgetData}
          highlightedPart={highlightedPart}
          format={format}
          isYTD
        />
        <ComparisonWidget
          data={quarterlyWidgetData}
          highlightedPart={highlightedPart}
          format={format}
        />
      </ComparisonWidgetsContainer>
      <div>
        <Collapsible
          wrapperClassName="small-table-collapsible-container"
          header={() => (
            <CollapsibleHeader>
              <span>Business unit</span>
              <div
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                }}
              >
                <ApSimpleDropdown
                  iconName="outline_save_alt"
                  onChange={(s: any) => {
                    if (s.value === 'xlsx') {
                      downloadSpendBreakdownBusinessUnitData(breakDownApi).then(
                        showSuccessNotification
                      );
                    }
                  }}
                  options={[EXPORT_OPTIONS[1]]}
                  label="Export"
                />
              </div>
            </CollapsibleHeader>
          )}
        >
          <BreakdownTable
            rowData={getTableData(
              breakdownBy.businessUnits,
              key,
              barData.labels
            )}
            columnTitles={barData.labels}
            columnDateLabels={tableDateLabels}
            format={format}
          />
        </Collapsible>
        <Collapsible
          wrapperClassName="small-table-collapsible-container"
          header={() => (
            <CollapsibleHeader>
              <span>Geography</span>
              <div
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                }}
              >
                <ApSimpleDropdown
                  iconName="outline_save_alt"
                  onChange={(s: any) => {
                    if (s.value === 'xlsx') {
                      downloadSpendGeographyData(
                        BREAKDOWN_BY_TO_API[selectedBreakdownByLocal]
                      ).then(showSuccessNotification);
                    }
                  }}
                  options={[EXPORT_OPTIONS[1]]}
                  label="Export"
                />
              </div>
            </CollapsibleHeader>
          )}
        >
          <BreakdownTable
            rowData={getTableData(breakdownBy.geographies, key, barData.labels)}
            columnTitles={barData.labels}
            columnDateLabels={tableDateLabels}
            format={format}
          />
        </Collapsible>
      </div>
    </SectionWrapper>
  );
};

export default ({
  breakdownBy,
  downloadSpendBreakdownChartData,
  downloadSpendBreakdownBusinessUnitData,
  downloadSpendGeographyData,
  isOnlyContribution,
  selectedBreakdownBy,
  showSuccessNotification,
  spendBy,
  updateSelectedBreakdownBy,
}: {
  isOnlyContribution: boolean;
  showSuccessNotification: () => void;
  breakdownBy?: IBreakdownByResponse;
  downloadSpendBreakdownChartData: (d: string) => Promise<any>;
  downloadSpendBreakdownBusinessUnitData: (d: string) => Promise<any>;
  downloadSpendGeographyData: (d: string) => Promise<any>;
  selectedBreakdownBy: string;
  spendBy: ISpendByType;
  updateSelectedBreakdownBy: (s: string) => void;
}) => (
  <QueryParams>
    {({ params }) => (
      <ApiContext.Consumer>
        {({ format, permissions }) => (
          <BreakDownByView
            breakdownBy={breakdownBy}
            downloadSpendBreakdownChartData={downloadSpendBreakdownChartData}
            downloadSpendBreakdownBusinessUnitData={
              downloadSpendBreakdownBusinessUnitData
            }
            downloadSpendGeographyData={downloadSpendGeographyData}
            format={format}
            isOnlyContribution={isOnlyContribution}
            params={params}
            permissions={permissions}
            selectedBreakdownBy={selectedBreakdownBy}
            showSuccessNotification={showSuccessNotification}
            spendBy={spendBy}
            updateSelectedBreakdownBy={updateSelectedBreakdownBy}
          />
        )}
      </ApiContext.Consumer>
    )}
  </QueryParams>
);
